import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import TitleAndBack from 'components/v2/TitleAndBack';
import { EpdLinks } from 'routes/EpdRoutes';
import {
  useGetEPDByVersion,
  useDictionaryValues,
  useMaterialPropertiesLookup,
  useReferencePackageVersions15804,
} from 'services/api/queries';
import { formatDate } from 'util/utils';
import VersionHistorySection from 'components/v2/compiler/version-history/VersionHistorySection';
import VersionHistorySummaryGrid, { SummaryGridRow } from 'components/v2/compiler/version-history/VersionHistorySummaryGrid';
import { Container } from 'styles/Styles.styled';
import { FieldPanelHeader, WizardHeaderGrid } from 'styles/v2/Styles.styled';
import SmartLabel from 'components/v2/labels/SmartLabel';
import FilesList from 'components/v2/file-row/FilesList';
import { MaterialPropertyItemModel, VerificationType } from 'services/EpdClient';
import { EPDBasedOnUnitEnum } from 'types/types';
import { ImagesUpload } from 'components/v2/images-upload';
import { useProductCategories } from 'services/api/standardEpdQueries';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import getVerificationTypeLabel from 'util/getVerificationTypeLabel';
import getSubtypeLabel from 'util/getSubtypeLabel';
import { NOT_ASSIGNED } from 'constants/constants';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import styled from 'styled-components';
interface RouteParams {
  epdId: string;
  versionId: string;
}

const val = (v: string | null | undefined) => v || NOT_ASSIGNED;
const formatDateValue = (value?: string | null) => (value ? (formatDate(value) as string) : NOT_ASSIGNED);

const EpdWizardVersionHistoryPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { epdId, versionId } = useParams<RouteParams>();
  const { data: epd, isLoading } = useGetEPDByVersion(epdId, versionId);
  const { data: allDictionaries = [] } = useDictionaryValues();
  const { data: { versions: referencePackageVersions15804 } = { versions: [] } } = useReferencePackageVersions15804();
  const { data: productCategoryOptions = [] } = useProductCategories();
  const materialPropertiesLookup = useMaterialPropertiesLookup().data;

  const biogenicCarbonContentUnits = allDictionaries
    .filter((d) => d.dictionaryCode === EPD_DICTIONARY_CODES.BIOGENIC_CARBON_CONTENT_UNITS)
    .map((x) => ({ value: x.key.toString(), label: x.value }));

  const referenceFlowUnitOptions = allDictionaries
    .filter((d) => d.dictionaryCode === EPD_DICTIONARY_CODES.REFERENCE_FLOW_UNIT)
    .map((x) => ({ value: x.value.toLowerCase(), label: x.description }));

  const conformityStandards = allDictionaries
    .filter((d) => d.dictionaryCode === EPD_DICTIONARY_CODES.CONFORMITY_STANDARDS)
    .map((x) => ({ value: x.key.toString(), label: x.value }));

  const epdBasedOnUnitOptions = allDictionaries.filter((d) => d.dictionaryCode === EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT);

  const formatReferenceFlow = (name?: string | null, flowPropertyId?: string | null, conversionFactor?: number | null) => {
    if (!name && !flowPropertyId && conversionFactor == null) {
      return NOT_ASSIGNED;
    }

    const unitLabel = flowPropertyId
      ? referenceFlowUnitOptions.find((o) => o.value === flowPropertyId)?.label ?? flowPropertyId
      : NOT_ASSIGNED;

    return [name || NOT_ASSIGNED, `${unitLabel}, ${conversionFactor ?? NOT_ASSIGNED}`].join('\n');
  };

  const formatBiogenicItem = (unitKey?: string, conversionFactor?: number) => {
    if (!unitKey || conversionFactor == null) {
      return NOT_ASSIGNED;
    }

    return `Unit: ${
      biogenicCarbonContentUnits.find((u) => u.value === unitKey?.toString())?.label ?? NOT_ASSIGNED
    }\nValue: ${conversionFactor}`;
  };

  const formatMaterialProperties = (materialProperties?: MaterialPropertyItemModel[]) => {
    if (!materialProperties?.length) {
      return NOT_ASSIGNED;
    }

    return (
      materialProperties
        .map((mp) => {
          const property = materialPropertiesLookup?.find((m) => m.id === mp.materialPropertyId);
          return property ? `Unit: ${property.fullName}\nValue: ${mp.value}` : NOT_ASSIGNED;
        })
        .join('\n\n') ?? NOT_ASSIGNED
    );
  };

  const formatDataSources = (
    dataSources?: {
      dataSource?: { name?: string };
      dataSourceVersion?: { name?: string };
    }[]
  ) => {
    if (!dataSources?.length) {
      return NOT_ASSIGNED;
    }

    return dataSources
      .map((x) => x.dataSourceVersion?.name)
      .filter(Boolean)
      .join('\n');
  };

  if (isLoading) {
    return (
      <Container>
        <Skeleton width="100%" height="4rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" />
      </Container>
    );
  }

  const datesRows: (SummaryGridRow | false)[] = [
    epd?.verificationType === VerificationType.EPDVerification &&
      !!epd?.approvalTimestamp && {
        name: 'Approval timestamp',
        value: formatDateValue(epd?.approvalTimestamp),
      },
    !!epd?.firstPublishedVersionNumber &&
      epd.firstPublishedVersionNumber < epd.versionNumber && {
        name: 'First version date',
        value: formatDateValue(epd?.firstPublishedVersionDate),
      },
    { name: 'Version date', value: formatDateValue(epd?.versionDate) },
    { name: 'Validity date', value: formatDateValue(epd?.validityDate) },
  ];

  const generalRows: (SummaryGridRow | false)[] = [
    { name: 'UUID of data set', value: val(epd?.dataSetId) },
    { name: 'Title', value: val(epd?.title) },
    { name: 'EPD ID', value: val(epd?.fullIdentificationNumber) },
    {
      name: 'EPD version',
      value: epd?.versionNumber != null ? epd.versionNumber.toString().padStart(3, '0') : NOT_ASSIGNED,
    },
    { name: 'Contact person', value: val(epd?.contactPerson?.contactName) },
    {
      name: 'LCA Practitioner',
      value: epd?.epdDevelopers?.length ? epd.epdDevelopers.map((d) => d.contactName).join(', ') : NOT_ASSIGNED,
    },
    {
      name: 'Verification type',
      value: epd?.verificationType ? getVerificationTypeLabel(epd.verificationType) : NOT_ASSIGNED,
    },
    epd?.verificationType === VerificationType.EPDVerification && {
      name: 'Verifier',
      value: val(epd?.epdVerifier?.displayName),
    },
    { name: 'Pre-verified tool', value: val(epd?.preverifiedTool?.name) },
    { name: 'Tool version', value: val(epd?.preverifiedToolVersion?.name) },
    { name: 'Tool Verifier', value: val(epd?.preverifiedToolVersion?.verifierName) },
    {
      name: 'Product category',
      value: epd?.productCategoryId
        ? productCategoryOptions.find((o) => o.id === epd.productCategoryId)?.title ?? val(epd.productCategoryId)
        : NOT_ASSIGNED,
    },
    { name: 'PCR', value: val(epd?.pcr?.fullName) },
    { name: 'c-PCR', value: val(epd?.cpcr?.fullName) },
    { name: 'Sector EPD', value: epd?.isSectorEPD ? 'Yes' : 'No' },
    { name: 'EPD Owner', value: val(epd?.epdOwner?.companyName) },
    { name: 'Country', value: val(epd?.epdOwner?.companyHomeCountry) },
    { name: 'Copyright', value: epd?.isProtectedByCopyright ? 'Yes' : 'No' },
    { name: 'Status', value: val(epd?.status?.toString()) },
  ];

  const productRows: SummaryGridRow[] = [
    {
      name: 'Collection(s)',
      value: epd?.collections?.length ? epd.collections.map((c) => c.name).join(', ') : NOT_ASSIGNED,
    },
    { name: 'Product description', value: val(epd?.productDescription), isHtmlRichText: true },
    { name: 'UN CPC code', value: val(epd?.uncpcCode?.description) },
    { name: 'Products', value: val(epd?.products), isHtmlRichText: true },
    {
      name: 'Geographical scope',
      value: epd?.geographicalScopes?.length ? epd.geographicalScopes.map((g) => g.name).join(', ') : NOT_ASSIGNED,
    },
    {
      name: 'Standards conformance',
      value: epd?.statementOfConformityWithStandards
        ? `In accordance with ${epd.statementOfConformityWithStandards
            .split(',')
            .map((key) => conformityStandards.find((o) => o.value === key.trim())?.label ?? key.trim())
            .join(', ')}`
        : NOT_ASSIGNED,
    },
  ];

  const machineReadableRows: (SummaryGridRow | false)[] = [
    { name: 'GTIN', value: val(epd?.gtin) },
    {
      name: 'EPD based on declared or functional unit',
      value: (() => {
        if (!epd?.epdBasedOnUnit) {
          return NOT_ASSIGNED;
        }
        const index = Object.keys(EPDBasedOnUnitEnum).indexOf(epd.epdBasedOnUnit);
        if (index === -1) {
          return 'Unsupported value';
        }
        return epdBasedOnUnitOptions.find((d) => d.key === index)?.value ?? NOT_ASSIGNED;
      })(),
    },
    epd?.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit && {
      name: 'Functional unit description',
      value: val(epd?.functionalUnitDescription),
    },
    {
      name:
        epd?.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit ? 'Reference flow' : 'Declared unit and reference flow',
      value: formatReferenceFlow(
        epd?.referenceFlow?.name,
        epd?.referenceFlow?.flowPropertyId,
        epd?.referenceFlow?.conversionFactor
      ),
      isHtmlRichText: true,
    },
    { name: 'Conversion factor to mass', value: val(epd?.conversionFactorToMass?.conversionFactor?.toString()) },
    {
      name: 'Mass of declared unit (kg)',
      value: epd?.massOfDeclaredUnit != null ? String(epd.massOfDeclaredUnit) : NOT_ASSIGNED,
    },
    { name: 'Product to declare', value: val(epd?.productType) },
    {
      name: 'Does the product contain more than 5% biogenic carbon?',
      value: val(epd?.biogenicCarbonContent?.doesProductContainMoreThan5Percent),
    },
    {
      name: 'Does the packaging material contain more than 5% biogenic carbon?',
      value: val(epd?.biogenicCarbonContent?.doesPackagingMaterialContainMoreThan5Percent),
    },
    {
      name: 'Biogenic carbon content of product',
      value: formatBiogenicItem(
        epd?.biogenicCarbonContent?.productItem?.unitKey,
        epd?.biogenicCarbonContent?.productItem?.conversionFactor
      ),
    },
    {
      name: 'Biogenic carbon content in accompanying package',
      value: formatBiogenicItem(
        epd?.biogenicCarbonContent?.accompanyingPackageItem?.unitKey,
        epd?.biogenicCarbonContent?.accompanyingPackageItem?.conversionFactor
      ),
    },
    {
      name: 'Material properties',
      value: formatMaterialProperties(epd?.materialProperties),
      isHtmlRichText: true,
    },
    { name: 'Geographical scope description', value: val(epd?.geographicalDescription) },
    { name: 'Technology description including background system', value: val(epd?.technologyDescription) },
    { name: 'Technical purpose of product or process', value: val(epd?.technicalPurpose) },
    {
      name: 'Subtype for ILCD+EPD',
      value: epd?.subtype != null ? getSubtypeLabel(epd.subtype, false) ?? epd?.subtype : NOT_ASSIGNED,
    },
    {
      name: 'Data sources used for this data set',
      value: (() => {
        const sources = formatDataSources(epd?.epdDataSources);
        if (epd?.isOtherDatabaseUsedAsDatasource) {
          const otherLine = `Other database: ${epd.otherDatabaseUsedAsDatasourceDescription || NOT_ASSIGNED}`;
          return sources !== NOT_ASSIGNED ? `${sources}\n\n${otherLine}` : otherLine;
        }
        return sources;
      })(),
    },
    {
      name: 'Version of the EN 15804 reference package',
      value: epd?.referencePackageVersion15804
        ? referencePackageVersions15804.find((o) => o.id === epd.referencePackageVersion15804)?.name ??
          val(epd.referencePackageVersion15804)
        : NOT_ASSIGNED,
    },
    { name: 'Characterisation methods', value: val(epd?.characterisationMethods) },
    {
      name: 'Additional information about the underlying LCA-based information',
      value: val(epd?.lcaBasedDescription),
      isHtmlRichText: true,
    },
    { name: 'Use advice for the EPD', value: val(epd?.useAdvice) },
  ];

  return (
    <PageWrapper>
      <Container>
        <WizardHeaderGrid>
          <TitleAndBack
            title={isLoading ? <Skeleton width="311px" height="34px" /> : epd?.fullIdentificationNumber}
            backTo="Back to the EPD"
            onBack={() => history.push(EpdLinks.epd(epdId))}
          />
        </WizardHeaderGrid>

        <VersionHistorySection title="EPD Dates">
          <VersionHistorySummaryGrid rows={datesRows.filter(Boolean) as SummaryGridRow[]} />
          <FieldPanelHeader style={{ marginTop: '20px' }}>General information</FieldPanelHeader>
          <VersionHistorySummaryGrid rows={generalRows.filter(Boolean) as SummaryGridRow[]} />
          {epd?.verificationReports && epd.verificationReports.length > 0 && (
            <div>
              <SmartLabel label="Verification report" />
              <FilesList files={epd.verificationReports} disabled />
            </div>
          )}
          {epd?.verificationType === VerificationType.EPDProcessCertification &&
            epd?.processCertificates &&
            epd.processCertificates.length > 0 && (
              <div>
                <SmartLabel label="Process certificate" />
                <FilesList files={epd.processCertificates} disabled />
              </div>
            )}
        </VersionHistorySection>

        <VersionHistorySection title="EPD Product information">
          <VersionHistorySummaryGrid rows={productRows} />
          <div>
            <SmartLabel label="Images" />
            <ImagesUpload
              name="ProductImages"
              images={epd?.productImages || []}
              onUpload={() => {}}
              onReorder={() => {}}
              onRemove={() => {}}
              disabled
              preferEnabledAppearanceEvenWhenDisabled
            />
          </div>
          {epd?.documents && epd.documents.filter((d) => d.name?.includes('EPD document')).length > 0 && (
            <div>
              <SmartLabel label="EPD document" />
              <FilesList files={epd.documents.filter((d) => d.name?.includes('EPD document'))} disabled />
            </div>
          )}
          {epd?.documents && epd.documents.filter((d) => d.name?.includes('Registration certificate')).length > 0 && (
            <div>
              <SmartLabel label="Registration certificate" />
              <FilesList files={epd.documents.filter((d) => d.name?.includes('Registration certificate'))} disabled />
            </div>
          )}
          {epd?.documents && epd.documents.filter((d) => d.name?.includes('Single-footprint report')).length > 0 && (
            <div>
              <SmartLabel label="Single-footprint report" />
              <FilesList files={epd.documents.filter((d) => d.name?.includes('Single-footprint report'))} disabled />
            </div>
          )}
        </VersionHistorySection>

        <VersionHistorySection title="Information for machine-readable EPDs">
          {epd?.lcAs && epd.lcAs.length > 0 && (
            <div>
              <SmartLabel label="LCA Results" />
              <FilesList files={epd.lcAs} disabled />
            </div>
          )}
          <VersionHistorySummaryGrid rows={machineReadableRows.filter(Boolean) as SummaryGridRow[]} />
        </VersionHistorySection>
      </Container>
    </PageWrapper>
  );
};

export default withGreyBackground(EpdWizardVersionHistoryPage);

const PageWrapper = styled.div`
  .p-datatable-tbody > tr > td,
  .p-datatable-tfoot > tr > td {
    font-size: 14px;
    line-height: 16px;
  }
`;
